import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { isCancel } from 'axios'
import { listCustomerAgents, listCustomers, setSubscriptionCapway, setSubscriptionPostnord } from '@/api/customers'
import { useAuthStore } from '@/store/authStore'
import { hasPermission } from '@/lib/permissions'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { FilterChips } from '@/components/FilterChips'
import { PaginationControls } from '@/components/PaginationControls'
import { PeriodMenu } from '@/components/PeriodMenu'
import { SortMenu } from '@/components/SortMenu'
import { CustomerFilterMenu } from '@/components/customer/CustomerFilterMenu'
import { CustomerSelectionToolbar } from '@/components/customer/CustomerSelectionToolbar'
import { CustomerTableRow } from '@/components/customer/CustomerTableRow'
import {
  canBulkActivate,
  getBulkActivationConfirmMessage,
  getBulkActivationErrorMessage,
  isEligibleForBulkActivation
} from '@/lib/bulkActivation'
import { listFacetChips, parseFacetFilters, toEnablePostnordParam, writeFacetFilters } from '@/lib/customerFilters'
import { AFTER_PARAM, BEFORE_PARAM, clearCursor, parseCursor, writeCursor } from '@/lib/pagination'
import { DEFAULT_SORT, LIST_SORT_OPTIONS, parseSort, writeSort } from '@/lib/sort'
import type { BulkActivationKind } from '@/lib/bulkActivation'
import type { CustomerFacetFilters } from '@/lib/customerFilters'
import type { DateRange, FilterChip } from '@/lib/filters'
import type { Customer, CustomerAgent, ListSort, Subscription } from '@/api/types'

const PAGE_SIZE = 100
const SEARCH_PARAM = 'search'

const ACTIVATE_REQUESTS: Record<BulkActivationKind, (customerId: number) => Promise<Subscription>> = {
  shipments: (customerId) => setSubscriptionPostnord(customerId, true),
  invoicing: (customerId) => setSubscriptionCapway(customerId, true)
}

// Tom söktext utelämnas, och ingen markör tas med – en ny sökning börjar alltid på första sidan.
function buildSearchParams(searchText: string, facets: CustomerFacetFilters, sort: ListSort): URLSearchParams {
  const params = new URLSearchParams()
  const search = searchText.trim()

  if (search !== '') {
    params.set(SEARCH_PARAM, search)
  }

  writeFacetFilters(params, facets)
  writeSort(params, sort)

  return params
}

function countEligible(customers: Customer[]): Record<BulkActivationKind, number> {
  return {
    shipments: customers.filter((customer) => isEligibleForBulkActivation('shipments', customer)).length,
    invoicing: customers.filter((customer) => isEligibleForBulkActivation('invoicing', customer)).length
  }
}

export function CustomersPage(): React.ReactElement {
  const user = useAuthStore((state) => state.user)
  const canManageSubscriptions = hasPermission(user, 'subscriptions.manage')
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchText, setSearchText] = useState('')
  const [agents, setAgents] = useState<CustomerAgent[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrevious, setHasPrevious] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [isActivating, setIsActivating] = useState(false)
  const [activationError, setActivationError] = useState<string | null>(null)

  const searchParam = searchParams.get(SEARCH_PARAM) ?? ''
  const after = parseCursor(searchParams.get(AFTER_PARAM))
  const before = parseCursor(searchParams.get(BEFORE_PARAM))
  // useSearchParams memoiserar på location.search, så facetterna byter identitet bara när URL:en gör det.
  const facetFilters = useMemo(() => parseFacetFilters(searchParams), [searchParams])
  const sort = parseSort(searchParams, LIST_SORT_OPTIONS, DEFAULT_SORT)

  const firstCustomerId = customers.at(0)?.id
  const lastCustomerId = customers.at(-1)?.id

  const facetChipSpecs = listFacetChips(facetFilters, agents)
  const hasFacetChips = facetChipSpecs.length > 0
  const hasSearch = searchParam !== ''
  const hasActiveFilters = hasSearch || hasFacetChips
  const hasError = errorMessage !== null
  const hasNoCustomers = !isLoading && !hasError && customers.length === 0
  const hasNextPage = !isLoading && hasNext && lastCustomerId !== undefined
  const hasPreviousPage = !isLoading && hasPrevious && firstCustomerId !== undefined
  const showPagination = !hasError && (customers.length > 0 || after !== undefined || before !== undefined)

  // Valet filtreras alltid genom raderna på skärmen, så ett id som överlever ett sidbyte är verkningslöst.
  const activatableCustomers = customers.filter(canBulkActivate)
  const selectedCustomers = customers.filter((customer) => selectedIds.has(customer.id))
  const selectedCount = selectedCustomers.length
  const eligibleCounts = countEligible(selectedCustomers)
  const hasActivatableCustomers = activatableCustomers.length > 0
  const isAllActivatableSelected = hasActivatableCustomers && activatableCustomers.every((customer) => selectedIds.has(customer.id))
  const hasActivationError = activationError !== null

  // Sökfältet följer URL:en (bakåt/framåt i webbläsaren), men inte markören – annars raderas halvskriven text vid sidbyte.
  useEffect(() => {
    setSearchText(searchParam)
  }, [searchParam])

  useEffect(() => {
    const loadAgents = async (): Promise<void> => {
      try {
        const result = await listCustomerAgents()

        setAgents(result)
      } catch {
        // Säljarlistan är bara ett stöd för filtret – utan den visas gruppen tom, resten av sidan fungerar.
        setAgents([])
      }
    }

    void loadAgents()
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    const loadCustomers = async (): Promise<void> => {
      setIsLoading(true)
      setErrorMessage(null)

      try {
        const result = await listCustomers(
          {
            search: searchParam || undefined,
            from: facetFilters.period.from || undefined,
            to: facetFilters.period.to || undefined,
            enable_postnord: toEnablePostnordParam(facetFilters.enablePostnord),
            subscription_status: facetFilters.subscriptionStatuses,
            validation_status: facetFilters.validationStatuses,
            agent_id: facetFilters.agentIds,
            sort,
            limit: PAGE_SIZE,
            after,
            before
          },
          controller.signal
        )

        setCustomers(result.customers)
        setTotalCount(result.total)
        setHasNext(result.has_next)
        setHasPrevious(result.has_previous)
        // Nytt resultat – tidigare val hör inte längre ihop med det som visas.
        setSelectedIds(new Set())
        setActivationError(null)
      } catch (error) {
        // En nyare sökning har redan tagit över – det här svaret ska varken visas eller felmarkeras.
        if (isCancel(error)) return

        setErrorMessage('Kunde inte hämta kunder just nu.')
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    void loadCustomers()

    return () => controller.abort()
  }, [searchParam, facetFilters, sort, after, before])

  const handleSearch = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    setSearchParams(buildSearchParams(searchText, facetFilters, sort))
  }

  // Nollställer även markören i URL:en – annars ligger pagineringen kvar på en sida som inte finns i den ofiltrerade listan.
  const handleReset = (): void => {
    setSearchText('')
    setSelectedIds(new Set())
    setActivationError(null)
    setSearchParams({})
  }

  // Facetter och period slår igenom direkt, till skillnad från sökfältet som väntar på Sök. Markören nollställs så listan börjar om på sida ett.
  const handleFacetsChange = (next: CustomerFacetFilters): void => {
    setSearchParams((previous) => {
      clearCursor(previous)
      writeFacetFilters(previous, next)

      return previous
    })
  }

  const handlePeriodChange = (period: DateRange): void => {
    handleFacetsChange({ ...facetFilters, period })
  }

  const handleSortChange = (next: ListSort): void => {
    setSearchParams((previous) => {
      clearCursor(previous)
      writeSort(previous, next)

      return previous
    })
  }

  const navigateToCursor = (cursorParam: string, customerId: number): void => {
    setSearchParams((previous) => {
      writeCursor(previous, cursorParam, customerId)

      return previous
    })
  }

  // "Nästa" bläddrar vidare i vald sortering och "Föregående" tillbaka mot listans början.
  const handlePreviousPage = (): void => {
    if (firstCustomerId === undefined) {
      return
    }

    navigateToCursor(BEFORE_PARAM, firstCustomerId)
  }

  const handleNextPage = (): void => {
    if (lastCustomerId === undefined) {
      return
    }

    navigateToCursor(AFTER_PARAM, lastCustomerId)
  }

  const handleToggleCustomer = (customerId: number): void => {
    setSelectedIds((current) => {
      const next = new Set(current)

      if (next.has(customerId)) {
        next.delete(customerId)
      } else {
        next.add(customerId)
      }

      return next
    })
  }

  const handleToggleAllActivatable = (): void => {
    if (isAllActivatableSelected) {
      setSelectedIds(new Set())
      return
    }

    setSelectedIds(new Set(activatableCustomers.map((customer) => customer.id)))
  }

  const handleClearSelection = (): void => {
    setSelectedIds(new Set())
  }

  const handleBulkActivate = async (kind: BulkActivationKind): Promise<void> => {
    const targets = selectedCustomers.filter((customer) => isEligibleForBulkActivation(kind, customer))

    if (targets.length === 0) return

    const isConfirmed = window.confirm(getBulkActivationConfirmMessage(kind, targets.length))

    if (!isConfirmed) return

    setIsActivating(true)
    setActivationError(null)

    const activate = ACTIVATE_REQUESTS[kind]
    const results = await Promise.allSettled(targets.map((customer) => activate(customer.id)))
    const updatedById = new Map<number, Customer>()
    let failedCount = 0

    // allSettled bevarar ordningen, så targets[i] hör ihop med results[i].
    targets.forEach((customer, index) => {
      const result = results[index]

      if (result?.status === 'fulfilled') {
        updatedById.set(customer.id, { ...customer, subscription: result.value })
        return
      }

      failedCount += 1
    })

    setCustomers((current) => current.map((customer) => updatedById.get(customer.id) ?? customer))
    // Bara de som inte längre har något kvar att aktivera avmarkeras. De som misslyckades, eller fortfarande
    // väntar på den andra åtgärden, ligger kvar markerade så nästa steg är ett klick bort.
    setSelectedIds((current) => {
      const remaining = [...current].filter((customerId) => {
        const updated = updatedById.get(customerId)

        return updated === undefined || canBulkActivate(updated)
      })

      return new Set(remaining)
    })

    if (failedCount > 0) {
      setActivationError(getBulkActivationErrorMessage(kind, failedCount, targets.length))
    }

    setIsActivating(false)
  }

  const filterChips: FilterChip[] = facetChipSpecs.map((chip) => ({
    key: chip.key,
    label: chip.label,
    onRemove: () => handleFacetsChange(chip.withoutChip)
  }))

  return (
    <div className="space-y-6">
      <Card className="shadow-none border-border/80">
        <CardHeader>
          <CardTitle className="text-3xl font-serif text-primary">Kundlista</CardTitle>
          <CardDescription>Tabell med sökning och filtrering.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSearch}
            className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto_auto_auto_auto]"
          >
            <Input
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Sök på namn, e-post, personnummer eller kundnummer"
            />
            <CustomerFilterMenu
              filters={facetFilters}
              agents={agents}
              onChange={handleFacetsChange}
            />
            <PeriodMenu
              range={facetFilters.period}
              onChange={handlePeriodChange}
            />
            <SortMenu
              value={sort}
              options={LIST_SORT_OPTIONS}
              onChange={handleSortChange}
            />
            <Button type="submit">Sök</Button>
            {hasActiveFilters && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleReset}
              >
                Rensa
              </Button>
            )}
          </form>

          {hasFacetChips && (
            <div className="mt-3">
              <FilterChips chips={filterChips} />
            </div>
          )}
        </CardContent>
      </Card>

      {hasError && (
        <Card className="shadow-none border-destructive/40">
          <CardContent className="pt-6 text-destructive">{errorMessage}</CardContent>
        </Card>
      )}

      {hasActivationError && (
        <Card className="shadow-none border-destructive/40">
          <CardContent className="pt-6 text-destructive">{activationError}</CardContent>
        </Card>
      )}

      {!hasError && (
        <Card className="shadow-none border-border/80">
          <CardContent className="p-0">
            <CustomerSelectionToolbar
              selectedCount={selectedCount}
              totalCount={totalCount}
              eligibleCounts={eligibleCounts}
              canManageSubscriptions={canManageSubscriptions}
              isActivating={isActivating}
              onClearSelection={handleClearSelection}
              onActivate={(kind) => void handleBulkActivate(kind)}
            />
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/50 text-left text-muted-foreground">
                  <tr>
                    {canManageSubscriptions && (
                      <th className="w-10 py-3 pl-6 pr-3 font-medium">
                        <input
                          type="checkbox"
                          aria-label="Markera alla som kan aktiveras"
                          className="size-4 align-middle accent-primary"
                          checked={isAllActivatableSelected}
                          disabled={!hasActivatableCustomers}
                          onChange={handleToggleAllActivatable}
                        />
                      </th>
                    )}
                    <th className="px-3 py-3 font-medium">Namn</th>
                    <th className="px-3 py-3 font-medium">E-post</th>
                    <th className="px-3 py-3 font-medium">Personnummer</th>
                    <th className="px-3 py-3 font-medium">Säljare</th>
                    <th className="px-3 py-3 font-medium text-center">Abonnemang</th>
                    <th className="px-3 py-3 font-medium text-center">Leveranser</th>
                    <th className="py-3 pl-3 pr-6 font-medium">Skapad</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <CustomerTableRow
                      key={customer.id}
                      customer={customer}
                      isSelected={selectedIds.has(customer.id)}
                      hasSelectionColumn={canManageSubscriptions}
                      onToggleSelected={handleToggleCustomer}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {isLoading && <p className="px-6 py-8 text-sm text-muted-foreground">Laddar kunder...</p>}
            {hasNoCustomers && <p className="px-6 py-8 text-sm text-muted-foreground">Inga kunder hittades.</p>}

            {showPagination && (
              <PaginationControls
                hasPreviousPage={hasPreviousPage}
                hasNextPage={hasNextPage}
                onPreviousPage={handlePreviousPage}
                onNextPage={handleNextPage}
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
